import { httpClient } from '@shared/infrastructure/http/http-client.js';
import { isControlKind } from '../lib/group-owner.js';
import type { CoherenceMetrics, CoherenceScope, MethodDelta } from '../model.js';

interface MethodPayload {
  incoherents_detected_pct?: number | null;
  truthful_flagged_pct?: number | null;
  correctness_pct?: number | null;
  incoherents_detected_over_all_pct?: number | null;
  false_positive_video_pct?: number | null;
}

interface MetricsPayload {
  scope?: {
    kind?: string;
    group_id?: number | null;
    control_kind?: string | null;
    series_key?: string | null;
  };
  global?: {
    videos_total?: number;
    videos_labeled?: number;
    videos_with_incoherence?: number;
    videos_truthful?: number;
    videos_evaluated?: number;
    videos_coherence_not_requested?: number;
    default_threshold_pct?: number;
    methods?: Record<string, MethodPayload>;
  };
  buckets?: {
    ok?: number;
    not_analysed?: number;
    degraded?: number;
    not_requested?: number;
  };
}

function toDelta(raw: MethodPayload): MethodDelta {
  return {
    incoherencesDetectedPct: raw.incoherents_detected_pct ?? null,
    falsePositivesPct: raw.truthful_flagged_pct ?? null,
    correctPct: raw.correctness_pct ?? null,
    incoherencesDetectedAllVideosPct: raw.incoherents_detected_over_all_pct ?? null,
    falsePositivesAllVideosPct: raw.false_positive_video_pct ?? null,
  };
}

/**
 * Boundary with the coherence metrics.
 *
 * Nothing here computes a percentage. Every figure is Virtual Profiler's own — a second
 * calculation of the same measurement gives a second answer, and then a screen has to choose
 * which of the two to believe.
 */
export const metricsApi = {
  read: async (scope: CoherenceScope = {}): Promise<CoherenceMetrics> => {
    const query = new URLSearchParams();
    if (scope.groupId !== undefined) query.set('groupId', String(scope.groupId));
    if (scope.controlKind !== undefined) query.set('controlKind', scope.controlKind);
    if (scope.threshold !== undefined) query.set('threshold', String(scope.threshold));
    if (scope.resolution !== undefined) query.set('resolution', scope.resolution);

    const payload = await httpClient.get<MetricsPayload>(
      `/virtual-profiler/metrics${query.size > 0 ? `?${query}` : ''}`,
    );
    const global = payload.global ?? {};

    return {
      scope: {
        kind:
          payload.scope?.kind === 'group' || payload.scope?.kind === 'control'
            ? payload.scope.kind
            : 'global',
        groupId: payload.scope?.group_id ?? null,
        controlKind: isControlKind(payload.scope?.control_kind) ? payload.scope.control_kind : null,
        seriesKey: payload.scope?.series_key ?? null,
      },
      thresholdPct: global.default_threshold_pct ?? 50,
      corpus: {
        total: global.videos_total ?? 0,
        labelled: global.videos_labeled ?? 0,
        withIncoherence: global.videos_with_incoherence ?? 0,
        truthful: global.videos_truthful ?? 0,
        evaluated: global.videos_evaluated ?? 0,
        coherenceNotRequested: global.videos_coherence_not_requested ?? 0,
      },
      buckets: {
        ok: payload.buckets?.ok ?? 0,
        notAnalysed: payload.buckets?.not_analysed ?? 0,
        degraded: payload.buckets?.degraded ?? 0,
        notRequested: payload.buckets?.not_requested ?? 0,
      },
      methods: Object.fromEntries(
        Object.entries(global.methods ?? {}).map(([method, raw]) => [method, toDelta(raw)]),
      ),
    };
  },
};
